import React, { useState, useEffect, useCallback } from "react";
import { Paper, Box, Typography, Button, IconButton, Tooltip, Divider } from "@mui/material";
import { Inventory2Outlined, Add, Remove, PhoneOutlined, VerifiedOutlined } from "@mui/icons-material";

export default function ProductBuyBox({
  selectedVariant,
  requestQuantity,
  setRequestQuantity,
  handleRequestInventoryCheck,
  requestLoading,
  requestAlreadySubmitted,
}) {
  const originalUnitPrice = Number(selectedVariant?.price || 0);
  const discountedUnitPrice = Number(selectedVariant?.discounted_price ?? selectedVariant?.price ?? 0);

  const hasDiscount = Boolean(selectedVariant?.has_discount) || (discountedUnitPrice > 0 && originalUnitPrice > 0 && discountedUnitPrice < originalUnitPrice);
  const unitPrice = hasDiscount ? discountedUnitPrice : originalUnitPrice;
  const discountPercent = Number(selectedVariant?.discount_percent || 0);

  const unitsPerPackage = Math.max(1, Number(selectedVariant?.units_per_package ?? 1));
  const stock = Number(selectedVariant?.stock);
  const hasKnownStock = selectedVariant?.stock != null && Number.isFinite(stock) && stock >= 0;
  const maxPackageQuantity = hasKnownStock ? Math.floor(stock / unitsPerPackage) : 0;

  const quantity = Math.max(1, Number(requestQuantity) || 1);

  const totalPrice = unitPrice * unitsPerPackage * quantity;
  const originalTotalPrice = originalUnitPrice * unitsPerPackage * quantity;

  const toToman = (amount) => Math.floor(Number(amount || 0));
  const formattedPrice = totalPrice ? toToman(totalPrice).toLocaleString("en-US") : "0";
  const formattedOriginalTotal = hasDiscount ? toToman(originalTotalPrice).toLocaleString("en-US") : null;

  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [showQtyInput, setShowQtyInput] = useState(false);
  const [qtyInputValue, setQtyInputValue] = useState(String(quantity));

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(([entry]) => setIsFooterVisible(entry.isIntersecting), { threshold: 0 });
      observer.observe(footer);
      return () => observer.disconnect();
    }
    const handleScroll = () => {
      const rect = footer.getBoundingClientRect();
      setIsFooterVisible(rect.top <= window.innerHeight);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setQtyInputValue(String(quantity));
  }, [quantity]);

  const handleQtyChange = useCallback((delta) => {
    const newQty = Math.max(1, Math.min(maxPackageQuantity || 99, quantity + delta));
    setRequestQuantity(newQty);
    setQtyInputValue(String(newQty));
  }, [quantity, maxPackageQuantity, setRequestQuantity]);

  const handleInputBlur = () => {
    const val = Math.max(1, Math.min(maxPackageQuantity || 99, Number(qtyInputValue) || 1));
    setRequestQuantity(val);
    setQtyInputValue(String(val));
    setShowQtyInput(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") e.currentTarget.blur();
  };

  const isDisabled = !selectedVariant || !hasKnownStock || maxPackageQuantity < 1 || quantity > maxPackageQuantity || requestLoading;
  const decDisabled = quantity <= 1;
  const incDisabled = !hasKnownStock || quantity >= maxPackageQuantity;

  return (
    <Paper
      elevation={0}
      sx={{
        position: { xs: "fixed", sm: "sticky" },
        bottom: { xs: 0, sm: "auto" },
        top: { sm: 16 },
        left: { xs: "50%", sm: "auto" },
        zIndex: { xs: 1200, sm: 1 },
        width: { xs: "100%", sm: "100%" },
        maxWidth: "100%",
        transform: { xs: isFooterVisible ? "translate(-50%, 110%)" : "translate(-50%, 0)", sm: "none" },
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: { xs: isFooterVisible ? "none" : "auto", sm: "auto" },
        p: 0,
        overflow: "hidden",
        borderRadius: { xs: "18px 18px 0 0", sm: 3 },
        bgcolor: "#fff",
        border: "1px solid #e9e2f3",
        borderBottom: { xs: "none", sm: "1px solid #e9e2f3" },
        boxShadow: {
          xs: "0 -10px 32px rgba(76, 41, 149, 0.16)",
          sm: "0 12px 32px rgba(76, 41, 149, 0.12)",
        },
      }}
    >
      {/* Premium accent bar */}
      <Box sx={{ height: 3, background: "linear-gradient(90deg, #7c5ac8 0%, #b79df0 55%, #ef4056 130%)" }} />

      <Box sx={{ p: { xs: 1.25, sm: 1.5 }, pb: { xs: "calc(10px + env(safe-area-inset-bottom, 0px))", sm: 1.5 } }}>
        {/* Seller-approval strip */}
        <Box
          display="flex"
          alignItems="center"
          gap={0.75}
          sx={{ mb: 1, px: 1, py: 0.6, borderRadius: 1.5, bgcolor: "#f6f2fd", border: "1px solid #e7defa" }}
        >
          <Inventory2Outlined sx={{ fontSize: 15, color: "#7c5ac8", flexShrink: 0 }} />
          <Typography variant="caption" fontWeight={600} color="#5b4a7a" sx={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "0.7rem" }}>
            موجودی و قیمت نیازمند تأیید فروشنده
          </Typography>
          <VerifiedOutlined sx={{ fontSize: 14, color: "#7c5ac8", flexShrink: 0 }} />
        </Box>

        {/* Price hero */}
        <Box display="flex" alignItems="flex-end" justifyContent="space-between" gap={1} sx={{ mb: 0.25 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.66rem", display: "block", mb: 0.15 }}>
              مبلغ قابل پرداخت
            </Typography>
            <Typography fontWeight={900} color={hasDiscount ? "#ef4056" : "#241443"} sx={{ fontSize: "1.45rem", lineHeight: 1.1, letterSpacing: "-0.5px", whiteSpace: "nowrap" }}>
              {formattedPrice}
              <Typography component="span" variant="caption" fontWeight={700} color="text.secondary" sx={{ ml: 0.5, fontSize: "0.7rem" }}>
                تومان
              </Typography>
            </Typography>
          </Box>
          {hasDiscount && (
            <Box display="flex" alignItems="center" gap={0.6} sx={{ flexShrink: 0, pb: 0.2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through", fontSize: "0.68rem", whiteSpace: "nowrap" }}>
                {formattedOriginalTotal}
              </Typography>
              <Tooltip title={`${discountPercent}٪ تخفیف اعمال شده`}>
                <Typography variant="caption" fontWeight={800} sx={{ bgcolor: "#ef4056", color: "#fff", borderRadius: 1.5, px: 0.8, py: 0.25, fontSize: "0.68rem", whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(239,64,86,0.35)" }}>
                  {discountPercent > 0 ? `٪${discountPercent} تخفیف` : "تخفیف‌دار"}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
        {unitsPerPackage > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.66rem", mb: 0.75 }}>
            بسته {unitsPerPackage.toLocaleString("en-US")} عددی · هر عدد {toToman(unitPrice).toLocaleString("en-US")} تومان
          </Typography>
        )}

        <Divider sx={{ my: 1, borderColor: "#f0eaf9" }} />

        {/* Segmented stepper */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="#4c3d6b" sx={{ fontSize: "0.72rem", flexShrink: 0 }}>
            تعداد بسته
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            sx={{ bgcolor: "#f6f2fd", border: "1px solid #e7defa", borderRadius: 2, p: 0.4, gap: 0.4 }}
          >
            <IconButton
              size="small"
              disabled={incDisabled}
              onClick={() => handleQtyChange(1)}
              aria-label="افزایش تعداد"
              sx={{
                width: 32, height: 32, minWidth: 32, borderRadius: 1.5, p: 0,
                bgcolor: incDisabled ? "transparent" : "#7c5ac8",
                color: incDisabled ? "#c3b8d9" : "#fff",
                boxShadow: incDisabled ? "none" : "0 2px 8px rgba(124,90,200,0.4)",
                "&:hover": { bgcolor: incDisabled ? "transparent" : "#6b4ab8" },
              }}
            >
              <Add sx={{ fontSize: 17 }} />
            </IconButton>

            {showQtyInput ? (
              <input
                type="number"
                value={qtyInputValue}
                onChange={(e) => setQtyInputValue(e.target.value)}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                min={1}
                max={maxPackageQuantity || 99}
                inputMode="numeric"
                autoFocus
                style={{ width: "52px", textAlign: "center", fontSize: "1rem", fontWeight: 800, color: "#241443", background: "transparent", border: "none", outline: "none", padding: "2px" }}
              />
            ) : (
              <Box onClick={() => setShowQtyInput(true)} sx={{ minWidth: 46, textAlign: "center", cursor: "pointer", px: 0.5 }}>
                <Typography fontWeight={900} color="#241443" sx={{ fontSize: "1.05rem", lineHeight: 1.2, userSelect: "none" }}>
                  {quantity}
                </Typography>
              </Box>
            )}

            <IconButton
              size="small"
              disabled={decDisabled}
              onClick={() => handleQtyChange(-1)}
              aria-label="کاهش تعداد"
              sx={{
                width: 32, height: 32, minWidth: 32, borderRadius: 1.5, p: 0,
                bgcolor: decDisabled ? "transparent" : "#fff",
                border: "1px solid", borderColor: decDisabled ? "transparent" : "#e2d7f5",
                color: decDisabled ? "#c3b8d9" : "#7c5ac8",
                boxShadow: decDisabled ? "none" : "0 1px 4px rgba(76,41,149,0.12)",
                "&:hover": { bgcolor: decDisabled ? "transparent" : "#efe8fb" },
              }}
            >
              <Remove sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>
        </Box>

        {!hasKnownStock || maxPackageQuantity === 0 ? (
          <Typography color="error" variant="caption" fontWeight={700} sx={{ display: "block", textAlign: "center", mb: 0.75, fontSize: "0.72rem" }}>
            ناموجود
          </Typography>
        ) : null}

        {/* Polished gradient CTA */}
        <Button
          variant="contained"
          fullWidth
          disabled={isDisabled}
          onClick={() => handleRequestInventoryCheck(quantity)}
          disableElevation
          sx={{
            minHeight: 46,
            borderRadius: 2.5,
            fontWeight: 800,
            fontSize: "0.87rem",
            textTransform: "none",
            py: 0,
            color: "#fff",
            bgcolor: isDisabled ? "#e8e0f0" : undefined,
            background: isDisabled ? undefined : "linear-gradient(135deg, #8b68d8 0%, #6d43c0 60%, #5b34a8 100%)",
            boxShadow: isDisabled ? "none" : "0 6px 18px rgba(109, 67, 192, 0.38)",
            transition: "transform 0.12s ease, box-shadow 0.2s ease, filter 0.2s ease",
            "&:hover": {
              background: isDisabled ? undefined : "linear-gradient(135deg, #9373db 0%, #744cC6 60%, #6240b0 100%)",
              boxShadow: isDisabled ? "none" : "0 8px 22px rgba(109, 67, 192, 0.45)",
              filter: "brightness(1.03)",
            },
            "&:active": { transform: isDisabled ? "none" : "scale(0.985)" },
          }}
        >
          {requestLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff", animation: "spin 0.8s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
              <span>در حال ثبت...</span>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <PhoneOutlined sx={{ fontSize: 18, flexShrink: 0 }} />
              <span>{requestAlreadySubmitted ? "ثبت / به‌روزرسانی درخواست" : "درخواست بررسی موجودی و قیمت"}</span>
            </Box>
          )}
        </Button>
      </Box>
    </Paper>
  );
}
